import { useEffect, useState } from 'react'
import { fxPurchasesApi } from '../../services/api'
import ExportBar, { ExportColumn } from '../../components/ExportBar'
import { useSortableData } from '../../hooks/useSortableData'
import SortableTh from '../../components/SortableTh'

interface Purchase {
  id: number
  supplier_id: number
  supplier_name: string
  product_id: number
  product_name: string
  quantity: number
  unit_cost: number
  discount_percentage: number | null
  purchase_date: string
  notes: string | null
  created_at: string
}

/** Read-only, frozen history of purchases registered under the old
 * single-product "Comprar stock" flow. New inventory is no longer bought
 * here -- see PurchaseOrdersPage ("Órdenes de Compra") for the current
 * multi-product, in-transit/on-hand purchasing flow. */
export default function FxPurchasesPage() {
  const [purchases, setPurchases] = useState<Purchase[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fxPurchasesApi
      .list()
      .then((res) => setPurchases(res.data.purchases || []))
      .catch((err) => console.error('Error fetching FX purchases:', err))
      .finally(() => setLoading(false))
  }, [])

  const { sortedData, sortConfig, requestSort } = useSortableData(purchases, {
    key: 'purchase_date',
    direction: 'desc',
  })

  const exportColumns: ExportColumn<Purchase>[] = [
    { key: 'purchase_date', label: 'Fecha', format: (p) => new Date(p.purchase_date).toLocaleDateString() },
    { key: 'product_name', label: 'Producto' },
    { key: 'supplier_name', label: 'Proveedor' },
    { key: 'quantity', label: 'Cantidad', format: (p) => String(p.quantity) },
    { key: 'unit_cost', label: 'Costo Unitario', format: (p) => `S/ ${p.unit_cost.toFixed(2)}` },
    { key: 'discount_percentage', label: 'Descuento', format: (p) => (p.discount_percentage ? `${p.discount_percentage}%` : '-') },
    { key: 'notes', label: 'Notas', format: (p) => p.notes || '-' },
  ]

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-espresso-800 mb-2">Compras — FX Admin</h1>
      <p className="text-sm text-espresso-500 mb-6">
        Historial de compras antiguas. Las compras nuevas ahora se registran desde "Órdenes de Compra".
      </p>

      {purchases.length === 0 ? (
        <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
          <p className="text-espresso-500">
            No hay compras registradas en este historial. Registra inventario nuevo desde "Órdenes de Compra".
          </p>
        </div>
      ) : (
        <>
          <ExportBar data={purchases} columns={exportColumns} filename="fx_compras" title="Compras FX Admin" />
          <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-espresso-100">
                <thead className="bg-cream-50">
                  <tr>
                    <SortableTh label="Fecha" sortKey="purchase_date" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Producto" sortKey="product_name" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Proveedor" sortKey="supplier_name" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Cantidad" sortKey="quantity" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Costo Unitario" sortKey="unit_cost" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableTh label="Descuento" sortKey="discount_percentage" sortConfig={sortConfig} onSort={requestSort} />
                    <th className="px-4 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Notas</th>
                  </tr>
                </thead>
                <tbody className="bg-cream-50 divide-y divide-espresso-100">
                  {sortedData.map((p) => (
                    <tr key={p.id} className="hover:bg-cream-100">
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-500">
                        {new Date(p.purchase_date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-espresso-800">{p.product_name}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">{p.supplier_name}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">{p.quantity}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-800">S/ {p.unit_cost.toFixed(2)}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-500">
                        {p.discount_percentage ? `${p.discount_percentage}%` : '-'}
                      </td>
                      <td className="px-4 py-4 text-sm text-espresso-500">{p.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
